import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {MetadataPanelComponent} from '../../shared/vte';
import {
    VisionProviderSyncReport,
    VisionSubscriptionConsistencyEntry,
    VisionSubscriptionConsistencyReport,
} from '../../models/vision-provider-management';
import {
    VisionProviderManagementViewService,
    VisionProviderSubscriptionApiService,
} from '../../services/vision-provider-management';
import {MetadataEntry} from '../../services/vte/vehicle-transaction-view.service';

@Component({
    selector: 'app-provider-diagnostics-tab',
    standalone: true,
    imports: [CommonModule, MetadataPanelComponent],
    templateUrl: './provider-diagnostics-tab.component.html',
    styleUrl: './provider-diagnostics-tab.component.css',
})
export class ProviderDiagnosticsTabComponent implements OnInit {
    providerId = '';
    diagnosticsEntries: MetadataEntry[] = [];
    syncEntries: MetadataEntry[] = [];
    consistencySummaryEntries: MetadataEntry[] = [];
    syncReport: VisionProviderSyncReport | null = null;
    consistencyReport: VisionSubscriptionConsistencyReport | null = null;
    consistencyLoadFailed = false;
    /** True after consistency GET succeeds or fails for the current load generation. */
    consistencyResolved = false;
    attentionEntries: VisionSubscriptionConsistencyEntry[] = [];
    loading = false;
    refreshing = false;

    /**
     * Monotonic generation for consistency fetches. Bumped when invalidating or
     * starting a fetch so out-of-order responses cannot overwrite current state.
     */
    private consistencyLoadGeneration = 0;

    constructor(
        private route: ActivatedRoute,
        private viewService: VisionProviderManagementViewService,
        private api: VisionProviderSubscriptionApiService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.route.parent?.paramMap.subscribe((params) => {
            this.providerId = params.get('providerId') || '';
            this.loadDiagnostics();
        });
    }

    get mismatch(): boolean {
        return this.consistencyReport?.mismatch === true;
    }

    get unverified(): boolean {
        if (this.mismatch) {
            return false;
        }
        if (this.consistencyLoadFailed) {
            return true;
        }
        return this.consistencyReport?.unverified === true;
    }

    loadDiagnostics(): void {
        if (!this.providerId) {
            return;
        }
        const providerId = this.providerId;
        this.loading = true;
        this.beginConsistencyPending();
        this.viewService.loadProviderSummary(providerId).subscribe({
            next: (summary) => {
                if (providerId !== this.providerId) {
                    return;
                }
                this.diagnosticsEntries = this.viewService.buildDiagnosticsEntries(summary.diagnostics);
                this.syncReport = this.viewService.getLastSyncReport(providerId);
                this.rebuildSyncEntries();
                this.loading = false;
                this.loadConsistencyReport();
            },
            error: () => {
                if (providerId !== this.providerId) {
                    return;
                }
                this.diagnosticsEntries = [];
                this.syncReport = null;
                this.rebuildSyncEntries();
                this.loading = false;
                this.loadConsistencyReport();
            },
        });
    }

    loadConsistencyReport(): void {
        if (!this.providerId) {
            return;
        }
        const providerId = this.providerId;
        const generation = this.beginConsistencyPending();
        this.api.getConsistencyReport(providerId).subscribe({
            next: (report) => {
                if (!this.isCurrentConsistencyRequest(providerId, generation)) {
                    return;
                }
                this.consistencyReport = report;
                this.consistencyLoadFailed = false;
                this.consistencyResolved = true;
                this.consistencySummaryEntries = this.buildConsistencySummaryEntries(report);
                this.attentionEntries = (report.entries || []).filter((entry) => entry.action_required);
                this.rebuildSyncEntries();
            },
            error: () => {
                if (!this.isCurrentConsistencyRequest(providerId, generation)) {
                    return;
                }
                this.consistencyReport = null;
                this.consistencyLoadFailed = true;
                this.consistencyResolved = true;
                this.consistencySummaryEntries = [];
                this.attentionEntries = [];
                this.rebuildSyncEntries();
            },
        });
    }

    refreshSynchronization(): void {
        if (!this.providerId) {
            return;
        }
        const providerId = this.providerId;
        this.refreshing = true;
        this.viewService.refreshRemoteComparison(providerId).subscribe({
            next: (report) => {
                if (providerId !== this.providerId) {
                    return;
                }
                this.refreshing = false;
                this.syncReport = this.viewService.getLastSyncReport(providerId);
                // Fail closed via loadConsistencyReport before presenting status.
                this.loadConsistencyReport();
                if (this.viewService.isComparisonAvailable(report)) {
                    this.toastr.success('Diagnostics synchronization refreshed.');
                    return;
                }
                this.toastr.warning(this.viewService.formatComparisonUnavailableMessage(report));
            },
            error: (error) => {
                if (providerId !== this.providerId) {
                    return;
                }
                this.refreshing = false;
                this.syncReport = this.viewService.getLastSyncReport(providerId);
                this.loadConsistencyReport();
                this.toastr.error(
                    this.viewService.formatVisionSubscriptionError(error, 'Unable to refresh diagnostics.'),
                );
            },
        });
    }

    categoryLabel(category: string): string {
        return this.consistencyReport?.category_labels?.[category] || category;
    }

    organizationLabel(entry: VisionSubscriptionConsistencyEntry): string {
        return this.viewService.formatOrganizationLabel(
            entry.organization_id,
            entry.organization_name,
        );
    }

    /**
     * Invalidate in-flight consistency work and fail closed to Unverified until
     * a current request resolves. Returns the generation assigned to the new load.
     */
    private beginConsistencyPending(): number {
        this.consistencyLoadGeneration += 1;
        this.consistencyResolved = false;
        this.consistencyLoadFailed = false;
        this.consistencyReport = null;
        this.consistencySummaryEntries = [];
        this.attentionEntries = [];
        this.rebuildSyncEntries();
        return this.consistencyLoadGeneration;
    }

    private isCurrentConsistencyRequest(providerId: string, generation: number): boolean {
        return generation === this.consistencyLoadGeneration && providerId === this.providerId;
    }

    private rebuildSyncEntries(): void {
        this.syncEntries = this.viewService.buildSyncEntries(
            this.syncReport,
            this.viewService.getLastSynchronizedAt(this.providerId),
            {
                report: this.consistencyReport,
                loadFailed: this.consistencyLoadFailed,
                resolved: this.consistencyResolved,
            },
        );
    }

    private buildConsistencySummaryEntries(
        report: VisionSubscriptionConsistencyReport,
    ): MetadataEntry[] {
        const counts = report.counts || {};
        return [
            {key: 'mismatch', label: 'Confirmed drift (mismatch)', value: report.mismatch ? 'Yes' : 'No'},
            {key: 'unverified', label: 'Unverified', value: report.unverified ? 'Yes' : 'No'},
            {
                key: 'remote_available',
                label: 'Remote inventory available',
                value: report.remote_available ? 'Yes' : 'No',
            },
            {
                key: 'remote_error',
                label: 'Remote error',
                value: report.remote_error || '—',
            },
            {
                key: 'desired_missing',
                label: 'Desired but not provisioned',
                value: String(counts['desired_missing'] ?? 0),
            },
            {
                key: 'kyra_only',
                label: 'KYRA only (remote missing)',
                value: String(counts['kyra_only'] ?? 0),
            },
            {
                key: 'een_only',
                label: 'EEN only (local missing)',
                value: String(counts['een_only'] ?? 0),
            },
            {
                key: 'unverified_count',
                label: 'Unverified entries',
                value: String(counts['unverified'] ?? 0),
            },
            {
                key: 'attention_count',
                label: 'Needs attention',
                value: String(report.attention_count ?? 0),
            },
            {
                key: 'generated_at',
                label: 'Checked at',
                value: this.viewService.formatTimestamp(report.generated_at),
            },
        ];
    }
}
